import React, { useState, useRef } from "react";
import { RefreshCw, Check, CheckCheck, CheckSquare, Copy, File, FileText, Forward, Pin, PinOff, X, Bell, Download, MoreVertical, Reply, ThumbsUp } from "lucide-react";
import CustomAudioPlayer from "../../../../../shared/ui/CustomAudioPlayer";
import { useWindowEvent } from "../../../../../shared/hooks/useWindowEvent";
import WhatsBookingSessionCard from "./WhatsBookingSessionCard";
import WhatsTaskCard from "./WhatsTaskCard";
import { formatJidAsPhone } from "./formatPhone";
import { useWhatsAppReactMessage, useWhatsAppPinMessage, useWhatsAppTask } from "../../../../client/hooks/useWhatsAppData";
import { assetUrl } from "../../../../../config";

const StickyPinnedItem = ({ msg, onClick }) => {
  if (!msg) return null;
  const displayText = msg.text_content ? msg.text_content.trim() : (
    msg.media_type === "imageMessage" || msg.media_type === "image" ? "📷 Foto" :
    msg.media_type === "videoMessage" || msg.media_type === "video" ? "🎥 Video" :
    msg.media_type === "audioMessage" || msg.media_type === "audio" ? "🎵 Audio" :
    "📄 Archivo"
  );

  return (
    <div 
      onClick={() => onClick && onClick(msg.msg_id)}
      style={{
        display: "flex",
        alignItems: "center",
        gap: "8px",
        backgroundColor: "rgba(255, 255, 255, 0.95)",
        padding: "6px 12px",
        borderRadius: "var(--radius-lg)",
        boxShadow: "0 1px 2px rgba(11,20,26,0.1)",
        cursor: "pointer",
        maxWidth: "220px",
        pointerEvents: "auto",
        border: "1px solid rgba(0,0,0,0.05)"
      }}
      title={displayText}
    >
      <span style={{
        fontSize: "13px",
        color: "#54656f",
        fontWeight: "500",
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        flex: 1
      }}>
        {displayText}
      </span>
      <div style={{
        width: "22px",
        height: "22px",
        borderRadius: "50%",
        backgroundColor: "#dcf8c6",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0
      }}>
        <Pin size={12} color="#00a884" style={{ transform: "rotate(45deg)" }} />
      </div>
    </div>
  );
};

const StickyTaskItem = ({ taskId, meetMembers, onClick }) => {
  const { data, isLoading, isError } = useWhatsAppTask(taskId);
  if (isLoading || isError || !data) return null;
  // Solo tareas pendientes
  if (data.checked || data.stage === "done") return null;

  const assigneeMember = meetMembers?.find(m => m.nick === data.assignee || m.name === data.assignee);
  const avatarUrl = assigneeMember?.image || assigneeMember?.avatar;

  return (
    <div 
      onClick={() => onClick && onClick(taskId)}
      style={{
        display: "flex",
        alignItems: "center",
        gap: "8px",
        backgroundColor: "rgba(255, 255, 255, 0.95)",
        padding: "6px 12px",
        borderRadius: "var(--radius-lg)",
        boxShadow: "0 1px 2px rgba(11,20,26,0.1)",
        cursor: "pointer",
        maxWidth: "220px",
        pointerEvents: "auto",
        border: "1px solid rgba(0,0,0,0.05)"
      }}
      title={data.title}
    >
      <span style={{
        fontSize: "13px",
        color: "#54656f",
        fontWeight: "500",
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        flex: 1
      }}>
        {data.title}
      </span>
      {avatarUrl ? (
        <img 
          src={avatarUrl.startsWith('http') ? avatarUrl : assetUrl(avatarUrl)} 
          alt={data.assignee} 
          style={{ width: "22px", height: "22px", borderRadius: "50%", objectFit: "cover", flexShrink: 0 }} 
        />
      ) : (
        <div style={{ width: "22px", height: "22px", borderRadius: "50%", backgroundColor: "#e2e8f0", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, fontSize: "10px", color: "#64748b", fontWeight: "600" }}>
          {(data.assignee || "?").charAt(0).toUpperCase()}
        </div>
      )}
    </div>
  );
};


const renderTime = (timestamp) => {
  if (!timestamp) return "";
  const date = new Date(timestamp);
  return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
};

// Convierte las URLs de un texto en `<a>` clickeables (integración main→dev, epic B2).
const URL_REGEX = /(https?:\/\/[^\s]+)/g;
const renderMessageTextWithLinks = (text) => {
  if (!text) return null;
  return text.split(URL_REGEX).map((part, index) => (
    part.match(URL_REGEX)
      ? <a key={index} href={part} target="_blank" rel="noopener noreferrer" style={{ color: "#3b82f6", textDecoration: "underline" }}>{part}</a>
      : <span key={index}>{part}</span>
  ));
};

const detectMedia = (msg) => {
  const url = msg.media_url || "";
  const t = msg.media_type;
  const isVideo = t === "videoMessage" || t === "video" || url.endsWith(".mp4") || url.includes(".mp4?");
  const isAudio = t === "audioMessage" || t === "audio" || /\.(mp3|wav|ogg|m4a)(\?.*)?$/i.test(url);
  const isImage = t === "imageMessage" || t === "image" || /\.(jpg|jpeg|png|gif|webp)(\?.*)?$/i.test(url);
  const fileName = url ? url.split("/").pop().split("?")[0] : "Archivo Adjunto";
  return { isVideo, isAudio, isImage, isOtherFile: !!url && !isVideo && !isImage && !isAudio, fileName, isPdf: fileName.toLowerCase().endsWith(".pdf") };
};

// 02.7-settings.md §5/paso b -- extraído de WhatsPanel.jsx. Presentacional: recibe la lista de
// mensajes ya armada por el container. B2 (integración main→dev): burbujas de media (imagen /
// video / audio / archivo), cita de mensaje, links clickeables, acciones responder/reenviar.
export default function WhatsMessageList({
  selectedChatJid, messages, loadingMessages, loadingMore, chats, isGroup, scrollContainerRef, onScroll, messagesEndRef,
  onReply, onForward, onOpenMedia, onOpenTask, onTaskFromMessage, userWhatsappJid, meetMembers
}) {
  const [openMenuId, setOpenMenuId] = useState(null);
  const [openReactionMenuId, setOpenReactionMenuId] = useState(null);
  const [highlightMsgId, setHighlightMsgId] = useState(null);
  const reactMutation = useWhatsAppReactMessage();
  const pinMutation = useWhatsAppPinMessage();

  const pinnedMessages = React.useMemo(() => {
    return messages?.filter(m => m.is_pinned) || [];
  }, [messages]);
  const latestPinned = pinnedMessages.length > 0 ? pinnedMessages[pinnedMessages.length - 1] : null;

  // Hallazgo de sesión (05-remediation.md, Fase 5.18): otro commit había resuelto el bloqueo del
  // gate de 5.16/5.17 con un `eslint-disable-next-line` sin motivo sobre un
  // `window.addEventListener` crudo -- el reemplazo sancionado (`useWindowEvent`, suscripción
  // compartida a nivel de módulo, usado en `WhatsPanel.jsx` para `hashchange`) ya cubre
  // exactamente este caso, sin necesitar ningún disable.
  useWindowEvent('click', () => setOpenMenuId(null));

  const handleCopy = (text) => {
    if (text) navigator.clipboard.writeText(text);
  };

  const taskIds = React.useMemo(() => {
    const ids = new Set();
    const taskRegex = /colonny\.xyz\/(?!p\/)(?:t|([a-zA-Z0-9_-]+))\/([a-fA-F0-9-]{36})/;
    messages?.forEach(msg => {
      if (msg.text_content) {
        const match = msg.text_content.match(taskRegex);
        if (match && match[2]) ids.add(match[2]);
      }
    });
    // Reverse so oldest is top, newest is bottom
    return Array.from(ids).reverse();
  }, [messages]);

  return (
    <div
      className="whats-messages-container"
      style={{ flex: 1, overflowY: "auto", position: "relative" }}
      ref={scrollContainerRef}
      onScroll={onScroll}
    >
      {/* Sticky Tasks & Pinned Items Container */}
      {(pinnedMessages.length > 0 || taskIds.length > 0) && (
        <div style={{ position: "sticky", top: "-4px", width: "100%", height: 0, zIndex: 10, pointerEvents: "none" }}>
          <div style={{ position: "absolute", right: "0", top: "0", display: "flex", flexDirection: "column", gap: "6px", alignItems: "flex-end", pointerEvents: "none" }}>
            {pinnedMessages.map(msg => (
              <StickyPinnedItem
                key={`pin-${msg.msg_id}`}
                msg={msg}
                onClick={(msgId) => {
                  const el = document.getElementById(`whats-msg-${msgId}`);
                  if (el) {
                    el.scrollIntoView({ behavior: "smooth", block: "center" });
                    setHighlightMsgId(msgId);
                    setTimeout(() => setHighlightMsgId(null), 2000);
                  }
                }}
              />
            ))}
            {taskIds.map(id => (
              <StickyTaskItem 
                key={`task-${id}`} 
                taskId={id} 
                meetMembers={meetMembers} 
                onClick={onOpenTask} 
              />
            ))}
          </div>
        </div>
      )}

      

      {loadingMore && (
        <div style={{ textAlign: "center", padding: "10px", color: "#888", fontSize: "12px" }}>
          <RefreshCw size={14} className="spin-animation" style={{ display: "inline-block", verticalAlign: "middle", marginRight: "5px" }} />
          Cargando mensajes anteriores...
        </div>
      )}
      {loadingMessages ? (
        <div style={{ textAlign: "center", padding: "var(--space-5)", color: "#888" }}>Cargando mensajes...</div>
      ) : (
        (() => {
          let lastDate = null;
          return messages.map((msg, index) => {
            const msgDate = new Date(msg.timestamp);
            const dateStr = msgDate.toLocaleDateString();
            let showDate = false;
            let dateLabel = "";
            if (dateStr !== lastDate) {
              showDate = true;
              lastDate = dateStr;
              const today = new Date();
              const yesterday = new Date();
              yesterday.setDate(yesterday.getDate() - 1);
              if (dateStr === today.toLocaleDateString()) dateLabel = "HOY";
              else if (dateStr === yesterday.toLocaleDateString()) dateLabel = "AYER";
              else dateLabel = msgDate.toLocaleDateString("es-ES", { day: "numeric", month: "short" }).toUpperCase();
            }
            const { isVideo, isAudio, isOtherFile, fileName, isPdf } = detectMedia(msg);
            return (
              <React.Fragment key={msg.msg_id || index}>
                {showDate && (
                  <div className="whats-date-divider" style={{ display: "flex", justifyContent: "flex-start", margin: "2px 0", paddingLeft: "10px", position: "sticky", top: "-4px", zIndex: 2 }}>
                    <span style={{ backgroundColor: "rgba(255, 255, 255, 0.95)", padding: "6px 14px", borderRadius: "var(--radius-lg)", fontSize: "12px", color: "#54656f", boxShadow: "0 1px 2px rgba(11,20,26,0.08)", fontWeight: "500" }}>
                      {dateLabel}
                    </span>
                  </div>
                )}
                <div id={`whats-msg-${msg.msg_id}`} className={`whats-message-row ${msg.from_me ? "sent" : "received"}`}>
                  <div 
                    className="whats-bubble" 
                    style={{ 
                      transition: "background-color 0.5s ease", 
                      backgroundColor: highlightMsgId === msg.msg_id ? "var(--color-primary-light, #d1fae5)" : undefined 
                    }}
                  >
                    {!msg.from_me && isGroup && (
                      <div className="whats-msg-sender" style={{ fontSize: "12px", color: "#f97316", fontWeight: "600", marginBottom: "var(--space-1)" }}>
                        {(() => {
                          const sJid = msg.sender_jid;
                          if (!sJid) return "Contacto";
                          const sChat = chats.find((c) => c.chat_jid === sJid);
                          return sChat?.name || formatJidAsPhone(sJid);
                        })()}
                      </div>
                    )}

                    {msg.is_forwarded && (
                      <div style={{ display: "flex", alignItems: "center", gap: "4px", color: "#8696a0", fontSize: "11.5px", fontStyle: "italic", marginBottom: "4px" }}>
                        <Forward size={12} />
                        <span>Reenviado</span>
                      </div>
                    )}

                    {msg.quoted_msg_id && (
                      <div
                        onClick={() => {
                          const el = document.getElementById(`whats-msg-${msg.quoted_msg_id}`);
                          if (el) {
                            el.scrollIntoView({ behavior: "smooth", block: "center" });
                            setHighlightMsgId(msg.quoted_msg_id);
                            setTimeout(() => setHighlightMsgId(null), 2000);
                          }
                        }}
                        style={{ backgroundColor: msg.from_me ? "rgba(0,0,0,0.08)" : "rgba(0,0,0,0.05)", borderLeft: "4px solid #00a884", padding: "6px 10px", borderRadius: "6px", marginBottom: "8px", cursor: "pointer", fontSize: "13px" }}
                      >
                        <div style={{ color: "#00a884", fontWeight: "bold", marginBottom: "4px", fontSize: "12px" }}>
                          {msg.quoted_sender_jid === userWhatsappJid ? "Tú" : (msg.quoted_sender_jid?.split("@")[0] || "Contacto")}
                        </div>
                        <div style={{ color: "#54656f", whiteSpace: "nowrap", textOverflow: "ellipsis", overflow: "hidden" }}>
                          {msg.quoted_msg_text || "Mensaje multimedia"}
                        </div>
                      </div>
                    )}

                    {msg.media_url && (
                      <div className="whats-msg-media" style={{ marginBottom: "5px", width: "100%" }}>
                        {isVideo ? (
                          <video controls src={msg.media_url} style={{ width: "100%", minWidth: "260px", borderRadius: "var(--radius-md)", maxHeight: "300px", objectFit: "cover" }} />
                        ) : isAudio ? (
                          <div style={{ width: "calc(100% + 16px)", minWidth: "260px", margin: "-6px -7px 4px -9px" }}>
                            <CustomAudioPlayer src={msg.media_url} />
                          </div>
                        ) : isOtherFile ? (
                          <div
                            onClick={() => onOpenMedia?.({ url: msg.media_url, filename: fileName, mime: msg.media_type })}
                            style={{ cursor: "pointer", backgroundColor: "#111b21", borderRadius: "var(--radius-md)", padding: "12px", display: "flex", alignItems: "center", gap: "12px", color: "white", width: "100%", minWidth: "260px", overflow: "hidden", boxSizing: "border-box" }}
                          >
                            <div style={{ padding: "8px", backgroundColor: "rgba(255,255,255,0.1)", borderRadius: "var(--radius-md)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                              {isPdf ? <FileText size={24} color="#ef4444" /> : <File size={24} color="#3b82f6" />}
                            </div>
                            <div style={{ flex: 1, minWidth: 0, overflow: "hidden" }}>
                              <div style={{ fontSize: "14px", fontWeight: "600", textOverflow: "ellipsis", whiteSpace: "nowrap", overflow: "hidden" }}>{fileName}</div>
                              <div style={{ fontSize: "12px", color: "#9ca3af" }}>
                                {msg.media_size ? `${(msg.media_size / 1024 / 1024).toFixed(2)} MB` : "Archivo adjunto"}
                              </div>
                            </div>
                            <a href={msg.media_url} download target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} style={{ color: "white", padding: "8px", display: "flex", alignItems: "center", justifyContent: "center" }}>
                              <Download size={20} />
                            </a>
                          </div>
                        ) : (
                          <img
                            onClick={() => onOpenMedia?.({ url: msg.media_url, filename: fileName, mime: msg.media_type })}
                            src={msg.media_url}
                            alt="Media"
                            style={{ cursor: "pointer", width: "100%", minWidth: "260px", borderRadius: "var(--radius-md)", maxHeight: "300px", objectFit: "cover" }}
                          />
                        )}
                      </div>
                    )}

                    <div className="whats-msg-text">
                      {renderMessageTextWithLinks(msg.text_content)}
                      {(() => {
                        if (!msg.text_content) return null;
                        
                        // Parse meet/call session link
                        const matchSession = msg.text_content.match(/colonny\.xyz\/p\/[^?]+\?(?:call|meet)\d+\/([A-Za-z0-9]{4,6})/);
                        if (matchSession && matchSession[1]) {
                          return <WhatsBookingSessionCard shortCode={matchSession[1]} isReceived={!msg.from_me} />;
                        }

                        // Parse task link
                        // Format: colonny.xyz/t/[uuid] or colonny.xyz/[space]/[uuid]
                        const matchTask = msg.text_content.match(/colonny\.xyz\/(?!p\/)(?:t|([a-zA-Z0-9_-]+))\/([a-fA-F0-9-]{36})/);
                        if (matchTask && matchTask[2]) {
                          return (
                            <WhatsTaskCard 
                              taskId={matchTask[2]} 
                              isReceived={!msg.from_me} 
                              onClick={() => onOpenTask && onOpenTask(matchTask[2], matchTask[1])}
                            />
                          );
                        }
                        
                        return null;
                      })()}
                    </div>

                    {msg.reactions && Object.keys(msg.reactions).length > 0 && (
                      <div style={{ display: "flex", gap: "4px", marginTop: "4px", flexWrap: "wrap", justifyContent: msg.from_me ? "flex-end" : "flex-start" }}>
                        {Object.entries(
                          Object.values(msg.reactions).reduce((acc, emoji) => {
                            acc[emoji] = (acc[emoji] || 0) + 1;
                            return acc;
                          }, {})
                        ).map(([emoji, count]) => (
                          <div key={emoji} style={{ 
                            backgroundColor: msg.from_me ? "rgba(255,255,255,0.3)" : "rgba(0,0,0,0.05)", 
                            borderRadius: "12px", 
                            padding: "2px 6px", 
                            fontSize: "12px",
                            display: "flex",
                            alignItems: "center",
                            gap: "4px",
                            border: "1px solid rgba(0,0,0,0.05)"
                          }}>
                            <span>{emoji}</span>
                            {count > 1 && <span style={{ fontSize: "10px", color: msg.from_me ? "#fff" : "#667781", fontWeight: "bold" }}>{count}</span>}
                          </div>
                        ))}
                      </div>
                    )}

                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "8px", minHeight: "19px" }}>
                      {msg.msg_id && msg.msg_id !== "__pending__" ? (
                        <div className="whats-msg-actions" style={{ display: "flex", gap: "8px", marginTop: "2px", position: "relative" }}>
                          {onReply && (
                            <button onClick={() => onReply(msg)} title="Responder" style={{ background: "none", border: "none", cursor: "pointer", color: "#8696a0", padding: 0 }}>
                              <Reply size={14} />
                            </button>
                          )}
                          <button 
                            onClick={(e) => {
                              e.stopPropagation();
                              setOpenReactionMenuId(openReactionMenuId === msg.msg_id ? null : msg.msg_id);
                              setOpenMenuId(null);
                            }} 
                            title="Reaccionar" 
                            style={{ background: "none", border: "none", cursor: "pointer", color: "#8696a0", padding: 0, opacity: 0.6 }}
                          >
                            <ThumbsUp size={14} />
                          </button>
                          {openReactionMenuId === msg.msg_id && (
                            <div 
                              onClick={(e) => e.stopPropagation()}
                              style={{ 
                                position: "absolute", left: msg.from_me ? "auto" : "0", right: msg.from_me ? "0" : "auto", bottom: "100%", marginBottom: "4px",
                                backgroundColor: "white", borderRadius: "16px", boxShadow: "0 2px 10px rgba(0,0,0,0.15)",
                                zIndex: 10, padding: "6px 12px",
                                display: "flex", gap: "12px",
                                border: "1px solid #e5e7eb"
                              }}
                            >
                              {['❤️', '🫡', '👍'].map(emoji => (
                                <span 
                                  key={emoji}
                                  onClick={() => {
                                    setOpenReactionMenuId(null);
                                    reactMutation.mutate({ jid: selectedChatJid, msgId: msg.msg_id, emoji, isFromMe: msg.from_me });
                                  }}
                                  style={{ cursor: "pointer", fontSize: "18px", transition: "transform 0.1s" }}
                                  onMouseEnter={(e) => e.target.style.transform = 'scale(1.2)'}
                                  onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
                                >
                                  {emoji}
                                </span>
                              ))}
                            </div>
                          )}
                          <button 
                            onClick={(e) => {
                              e.stopPropagation();
                              setOpenMenuId(openMenuId === msg.msg_id ? null : msg.msg_id);
                              setOpenReactionMenuId(null);
                            }} 
                            title="Más opciones" 
                            style={{ background: "none", border: "none", cursor: "pointer", color: "#8696a0", padding: 0 }}
                          >
                            <MoreVertical size={14} />
                          </button>
                          
                          {openMenuId === msg.msg_id && (
                            <div 
                              onClick={(e) => e.stopPropagation()}
                              style={{ 
                                position: "absolute", left: msg.from_me ? "auto" : "0", right: msg.from_me ? "0" : "auto", bottom: "100%", marginBottom: "4px",
                                backgroundColor: "white", borderRadius: "var(--radius-md)", boxShadow: "0 2px 10px rgba(0,0,0,0.15)",
                                minWidth: "150px", zIndex: 10, padding: "4px 0",
                                display: "flex", flexDirection: "column"
                              }}
                            >
                              <button 
                                onClick={() => { 
                                  setOpenMenuId(null); 
                                  pinMutation.mutate({
                                    jid: selectedChatJid,
                                    msgId: msg.msg_id,
                                    isPinned: !msg.is_pinned,
                                    isFromMe: msg.from_me
                                  });
                                }} 
                                style={{ display: "flex", alignItems: "center", gap: "10px", padding: "10px 16px", background: "none", border: "none", cursor: "pointer", width: "100%", textAlign: "left", fontSize: "14px", color: msg.is_pinned ? "#ef4444" : "#374151" }}
                              >
                                {msg.is_pinned ? (
                                  <><PinOff size={16} color="#ef4444" /> Desfijar</>
                                ) : (
                                  <><Pin size={16} color="#6b7280" /> Fijar</>
                                )}
                              </button>
                              {onForward && (
                                <button onClick={() => { setOpenMenuId(null); onForward(msg); }} style={{ display: "flex", alignItems: "center", gap: "10px", padding: "10px 16px", background: "none", border: "none", cursor: "pointer", width: "100%", textAlign: "left", fontSize: "14px", color: "#374151" }}>
                                  <Forward size={16} color="#6b7280" /> Reenviar
                                </button>
                              )}
                              <button onClick={() => { setOpenMenuId(null); handleCopy(msg.text_content); }} style={{ display: "flex", alignItems: "center", gap: "10px", padding: "10px 16px", background: "none", border: "none", cursor: "pointer", width: "100%", textAlign: "left", fontSize: "14px", color: "#374151" }}>
                                <Copy size={16} color="#6b7280" /> Copiar
                              </button>
                              <button 
                                onClick={() => { 
                                  setOpenMenuId(null); 
                                  onTaskFromMessage && onTaskFromMessage(msg);
                                }} 
                                style={{ display: "flex", alignItems: "center", gap: "10px", padding: "10px 16px", background: "none", border: "none", cursor: "pointer", width: "100%", textAlign: "left", fontSize: "14px", color: "#374151" }}
                              >
                                <CheckSquare size={16} color="#6b7280" /> Tarea
                              </button>
                              <button onClick={() => { setOpenMenuId(null); /* placeholder */ }} style={{ display: "flex", alignItems: "center", gap: "10px", padding: "10px 16px", background: "none", border: "none", cursor: "pointer", width: "100%", textAlign: "left", fontSize: "14px", color: "#374151" }}>
                                <Bell size={16} color="#6b7280" /> Recordatorio
                              </button>
                            </div>
                          )}
                        </div>
                      ) : <div />}
                      <div className="whats-msg-meta" style={{ display: "flex", alignItems: "center", gap: "var(--space-1)" }}>
                        {msg.is_pinned && (
                          <Pin size={11} color="#00a884" style={{ transform: "rotate(45deg)", marginRight: "2px" }} title="Mensaje fijado" />
                        )}
                        <span className="whats-msg-time">{renderTime(msg.timestamp)}</span>
                        {msg.from_me && (
                          msg.status === "read" ? <CheckCheck size={14} color="#3b82f6" style={{ marginTop: "2px" }} />
                            : msg.status === "delivered" ? <CheckCheck size={14} color="#9ca3af" style={{ marginTop: "2px" }} />
                              : <Check size={14} color="#9ca3af" style={{ marginTop: "2px" }} />
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </React.Fragment>
            );
          });
        })()
      )}
      <div ref={messagesEndRef} />
    </div>
  );
}
